import { useState, type ComponentType, type ReactNode } from 'react';
import { ChevronUp } from 'lucide-react';

export interface SidebarUserAction {
  label: string;
  icon: ComponentType<{ size?: number; className?: string }>;
  onClick: () => void;
  /** Red treatment (sign-out). */
  danger?: boolean;
}

export interface SidebarUserCardProps {
  name: string;
  role?: string;
  photoURL?: string | null;
  /** Shown as rows when the card is expanded via the chevron. */
  actions?: SidebarUserAction[];
  /** Extra content shown above the actions when expanded. */
  children?: ReactNode;
}

/**
 * Calm-v2 sidebar footer: avatar + name/role, with a chevron that expands
 * to reveal the user actions (theme, sign-out, …). Same pattern as the team
 * app's drawer user card.
 */
export function SidebarUserCard({ name, role, photoURL, actions, children }: SidebarUserCardProps) {
  const [open, setOpen] = useState(false);
  const expandable = (actions?.length ?? 0) > 0 || !!children;

  return (
    <div className="flex w-full flex-col">
      <button
        type="button"
        onClick={expandable ? () => setOpen((v) => !v) : undefined}
        aria-expanded={expandable ? open : undefined}
        className={`flex w-full items-center gap-2.5 rounded-lg px-1 py-1 text-right ${
          expandable ? 'hover:bg-surface-soft cursor-pointer transition-colors' : 'cursor-default'
        }`}
      >
        {photoURL ? (
          <img src={photoURL} alt="" className="h-[30px] w-[30px] shrink-0 rounded-full" />
        ) : (
          <div className="bg-brand flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
            {name.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-ink truncate text-xs font-bold">{name}</div>
          {role && <div className="text-muted text-[10px]">{role}</div>}
        </div>
        {expandable && (
          <ChevronUp
            size={16}
            className={`text-muted shrink-0 transition-transform duration-200 ${open ? '' : 'rotate-180'}`}
          />
        )}
      </button>

      {open && (
        <div className="border-line mt-2 flex flex-col gap-0.5 border-t pt-2">
          {children}
          {actions?.map((a) => (
            <button
              key={a.label}
              type="button"
              onClick={a.onClick}
              className={`rounded-control flex min-h-9 w-full items-center gap-2.5 px-2 text-xs font-semibold transition-colors ${
                a.danger
                  ? 'text-danger hover:bg-danger-soft'
                  : 'text-ink-2 hover:bg-surface-soft hover:text-ink'
              }`}
            >
              <a.icon size={16} className={a.danger ? 'scale-x-[-1]' : undefined} />
              <span>{a.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
